'use client'

import config from '@/amplify_outputs.json'

type AmplifyOutputAuthConfig = {
  identity_pool_id?: unknown
  user_pool_client_id?: unknown
}

function getAmplifyOutputAuthConfig(
  value: unknown,
): AmplifyOutputAuthConfig | undefined {
  if (!value || typeof value !== 'object') return undefined

  const auth = (value as { auth?: unknown }).auth
  if (!auth || typeof auth !== 'object') return undefined

  return auth as AmplifyOutputAuthConfig
}

const authConfig = getAmplifyOutputAuthConfig(config)
const USER_POOL_CLIENT_ID =
  typeof authConfig?.user_pool_client_id === 'string'
    ? authConfig.user_pool_client_id
    : ''
const IDENTITY_POOL_ID =
  typeof authConfig?.identity_pool_id === 'string'
    ? authConfig.identity_pool_id
    : ''
const AMPLIFY_AUTH_NAMESPACE_PREFIX = 'CognitoIdentityServiceProvider.'
const AMPLIFY_IDENTITY_NAMESPACE_PREFIX = 'com.amplify.Cognito.'

export const AMPLIFY_AUTH_STORAGE_PREFIX = `CognitoIdentityServiceProvider.${USER_POOL_CLIENT_ID}.`
export const AMPLIFY_LAST_AUTH_USER_KEY = `CognitoIdentityServiceProvider.${USER_POOL_CLIENT_ID}.LastAuthUser`
export const AMPLIFY_IDENTITY_ID_KEY = `com.amplify.Cognito.${IDENTITY_POOL_ID}.identityId`
export const AMPLIFY_SIGN_IN_STATE_KEYS = [
  'CognitoSignInState.username',
  'CognitoSignInState.challengeName',
  'CognitoSignInState.signInSession',
  'CognitoSignInState.expiry',
] as const

export type AmplifyAuthStorageDiagnostics = {
  auth_cookie_count: number
  auth_cookie_bytes: number
  auth_cookie_largest_bytes: number
  auth_local_storage_count: number
  auth_session_storage_count: number
  auth_cookie_readable: boolean
  auth_local_storage_readable: boolean
  auth_session_storage_readable: boolean
  cookie_enabled: boolean
  secure_context: boolean
}

function isAmplifyAuthArtifactKey(key: string): boolean {
  return (
    key.startsWith(AMPLIFY_AUTH_NAMESPACE_PREFIX) ||
    (key.startsWith(AMPLIFY_IDENTITY_NAMESPACE_PREFIX) &&
      key.endsWith('.identityId')) ||
    AMPLIFY_SIGN_IN_STATE_KEYS.some((signInKey) => signInKey === key)
  )
}

function getAmplifyAuthArtifactCookieName(rawName: string): string | undefined {
  if (isAmplifyAuthArtifactKey(rawName)) return rawName

  try {
    const decodedName = decodeURIComponent(rawName)
    return isAmplifyAuthArtifactKey(decodedName) ? decodedName : undefined
  } catch {
    return undefined
  }
}

function removeMatchingStorageKeys(
  storage: Storage | null | undefined,
  predicate: (key: string) => boolean,
): void {
  if (!storage) return

  try {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    ).filter((key): key is string => Boolean(key))

    for (const key of keys) {
      if (predicate(key)) {
        storage.removeItem(key)
      }
    }
  } catch {}
}

function expireCookie(name: string): void {
  if (typeof document === 'undefined') return

  const variants = [name, encodeURIComponent(name)]
  const cookieDirectives = [
    'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/',
    'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/; SameSite=Lax',
  ]

  // Storefront shares this Cognito client on .skop.app. Host-only expiry
  // cannot remove a revoked shared token. Do not clear other shared clients.
  const hostname = document.location?.hostname ?? ''
  if (
    USER_POOL_CLIENT_ID &&
    name.startsWith(AMPLIFY_AUTH_STORAGE_PREFIX) &&
    (hostname === 'skop.app' || hostname.endsWith('.skop.app'))
  ) {
    cookieDirectives.push(
      'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/; Domain=.skop.app',
    )
  }

  for (const variant of variants) {
    for (const directive of cookieDirectives) {
      document.cookie = `${variant}=; ${directive}`
    }
  }
}

function removeMatchingCookies(predicate: (name: string) => boolean): void {
  if (typeof document === 'undefined') return

  try {
    const cookieNames = document.cookie
      .split(';')
      .map((entry) => entry.trim().split('=')[0])
      .filter(Boolean)

    for (const rawName of cookieNames) {
      const artifactName = getAmplifyAuthArtifactCookieName(rawName)
      if (artifactName && predicate(artifactName)) {
        expireCookie(artifactName)
        if (rawName !== artifactName) {
          expireCookie(rawName)
        }
      }
    }
  } catch {}
}

function getStorageSafely(
  kind: 'localStorage' | 'sessionStorage',
): Storage | undefined {
  try {
    return globalThis[kind]
  } catch {
    return undefined
  }
}

function getStorageDiagnostics(kind: 'localStorage' | 'sessionStorage'): {
  count: number
  readable: boolean
} {
  const storage = getStorageSafely(kind)
  if (!storage) return { count: 0, readable: false }

  try {
    let count = 0
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key && isAmplifyAuthArtifactKey(key)) {
        count += 1
      }
    }
    return { count, readable: true }
  } catch {
    return { count: 0, readable: false }
  }
}

function byteLength(value: string): number {
  try {
    return new TextEncoder().encode(value).byteLength
  } catch {
    return value.length
  }
}

function getCookieDiagnostics(): {
  count: number
  bytes: number
  largestBytes: number
  readable: boolean
} {
  if (typeof document === 'undefined') {
    return { count: 0, bytes: 0, largestBytes: 0, readable: false }
  }

  try {
    let count = 0
    let bytes = 0
    let largestBytes = 0

    for (const entry of document.cookie.split(';')) {
      const normalizedEntry = entry.trim()
      if (!normalizedEntry) continue

      const separatorIndex = normalizedEntry.indexOf('=')
      const rawName =
        separatorIndex === -1
          ? normalizedEntry
          : normalizedEntry.slice(0, separatorIndex)

      if (!getAmplifyAuthArtifactCookieName(rawName)) continue

      const entryBytes = byteLength(normalizedEntry)
      count += 1
      bytes += entryBytes
      largestBytes = Math.max(largestBytes, entryBytes)
    }

    return { count, bytes, largestBytes, readable: true }
  } catch {
    return { count: 0, bytes: 0, largestBytes: 0, readable: false }
  }
}

/**
 * Returns aggregate storage metadata only. Cookie names and values can contain
 * identity data and tokens, so neither is returned or logged.
 */
export function collectAmplifyAuthStorageDiagnostics(): AmplifyAuthStorageDiagnostics {
  const cookies = getCookieDiagnostics()
  const local = getStorageDiagnostics('localStorage')
  const session = getStorageDiagnostics('sessionStorage')

  return {
    auth_cookie_count: cookies.count,
    auth_cookie_bytes: cookies.bytes,
    auth_cookie_largest_bytes: cookies.largestBytes,
    auth_local_storage_count: local.count,
    auth_session_storage_count: session.count,
    auth_cookie_readable: cookies.readable,
    auth_local_storage_readable: local.readable,
    auth_session_storage_readable: session.readable,
    cookie_enabled:
      typeof navigator !== 'undefined' ? navigator.cookieEnabled : false,
    secure_context:
      typeof globalThis.isSecureContext === 'boolean'
        ? globalThis.isSecureContext
        : false,
  }
}

export function clearAmplifyBrowserAuthArtifacts(): boolean {
  const localStorageRef = getStorageSafely('localStorage')
  const sessionStorageRef = getStorageSafely('sessionStorage')

  // The current origin is owned by Manage. Clear all Amplify auth namespaces,
  // including artifacts left by a previous Cognito app client configuration.
  removeMatchingStorageKeys(localStorageRef, isAmplifyAuthArtifactKey)
  removeMatchingStorageKeys(sessionStorageRef, isAmplifyAuthArtifactKey)
  removeMatchingCookies(isAmplifyAuthArtifactKey)

  const diagnostics = collectAmplifyAuthStorageDiagnostics()
  return (
    diagnostics.auth_cookie_readable &&
    diagnostics.auth_local_storage_readable &&
    diagnostics.auth_session_storage_readable &&
    diagnostics.auth_cookie_count === 0 &&
    diagnostics.auth_local_storage_count === 0 &&
    diagnostics.auth_session_storage_count === 0
  )
}
